import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Star, Sparkles, X, Quote } from "lucide-react";

const TABS = [
  { key: "featured", label: "Featured" },
  { key: "recent",   label: "Recently Submitted" },
  { key: "top",      label: "Most Voted" },
];

export default function TruthWall() {
  const [page, setPage] = useState(null);
  const [tab, setTab] = useState("featured");
  const [truths, setTruths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [voted, setVoted] = useState(() => new Set(JSON.parse(localStorage.getItem("tw_voted") || "[]")));

  // Load page CMS once
  useEffect(() => {
    http.get("/truth-wall/page").then(({ data }) => setPage(data)).catch(() => {});
  }, []);

  // Load truths whenever the tab changes
  useEffect(() => {
    setLoading(true);
    http.get("/truth-wall/truths", { params: { filter: tab, limit: 60 } })
      .then(({ data }) => setTruths(data || []))
      .catch(() => toast.error("Could not load truths."))
      .finally(() => setLoading(false));
  }, [tab]);

  const persistVote = (id) => {
    const next = new Set([...voted, id]);
    setVoted(next);
    localStorage.setItem("tw_voted", JSON.stringify([...next]));
  };

  const vote = async (t) => {
    if (voted.has(t.id)) { toast("You already voted on this one."); return; }
    try {
      const { data } = await http.post(`/truth-wall/truths/${t.id}/vote`);
      persistVote(t.id);
      setTruths((arr) => arr.map((x) => x.id === t.id ? { ...x, votes: data.votes ?? (x.votes || 0) + 1 } : x));
      toast.success("Thanks for the vote.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't record vote.");
    }
  };

  if (!page) return <Layout><div className="min-h-[60vh] flex items-center justify-center text-[#8a857d]">Loading…</div></Layout>;

  if (page.enabled === false) {
    return (
      <Layout>
        <section className="min-h-[60vh] flex items-center justify-center bg-[#050505] px-6 py-24">
          <div className="text-center max-w-md">
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">{page.subtitle}</div>
            <h1 className="font-display text-4xl text-[#F4F1EA] mb-4">{page.title}</h1>
            <div className="w-12 h-px bg-[#C9A055] mx-auto mb-5" />
            <p className="font-body text-[15px] text-[#B5B0A7]">Coming soon. We&rsquo;re putting the finishing touches on this part of the movement.</p>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="tw-hero">
        {page.hero_image ? (
          <div className="absolute inset-0">
            <img src={resolveImage(page.hero_image)} alt="" className="w-full h-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/70 to-[#050505]" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-[#050505]" />
        )}
        <div className="relative max-w-3xl mx-auto px-6 py-24 sm:py-36 text-center">
          <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-5">
            {page.subtitle}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F4F1EA] leading-[1.05] mb-7"
              data-testid="tw-title">
            {page.title}
          </h1>
          <p className="font-body text-[15px] leading-[1.8] text-[#B5B0A7] max-w-2xl mx-auto">
            {page.intro}
          </p>
          <div className="mt-9 flex flex-wrap gap-3 justify-center">
            <button onClick={() => setSubmitOpen(true)} data-testid="tw-submit-btn" className="btn-gold">
              {page.submit_cta}
            </button>
            <button onClick={() => { setTab("featured"); document.getElementById("tw-list")?.scrollIntoView({ behavior: "smooth" }); }}
                    className="btn-outline">
              {page.view_cta}
            </button>
          </div>
        </div>
      </section>

      {/* TABS + LIST */}
      <section id="tw-list" className="bg-[#0a0a0a] py-14 sm:py-20 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-wrap gap-2 justify-center mb-10" data-testid="tw-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tw-tab-${t.key}`}
                className={`font-ui text-[10px] tracking-[0.22em] uppercase px-4 py-2 border transition ${
                  tab === t.key
                    ? "bg-[#C9A055] text-black border-[#C9A055]"
                    : "border-white/15 text-[#8a857d] hover:text-[#F4F1EA] hover:border-[#C9A055]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center text-[#8a857d] font-body">Loading…</div>
          ) : truths.length === 0 ? (
            <div className="text-center border border-dashed border-white/10 bg-[#050505] p-12">
              <Quote size={28} className="mx-auto text-[#C9A055] opacity-60 mb-3" />
              <div className="font-display text-lg text-[#F4F1EA] mb-1">No truths yet here.</div>
              <div className="font-body text-sm text-[#8a857d]">Be the first to share.</div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tw-truths-grid">
              {truths.map((t) => (
                <TruthCard
                  key={t.id}
                  t={t}
                  voted={voted.has(t.id)}
                  showVotes={page.show_votes}
                  votingEnabled={page.voting_enabled}
                  onVote={() => vote(t)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* GUIDELINES */}
      <section className="bg-[#050505] py-14 sm:py-20 border-t border-white/5" data-testid="tw-guidelines">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">Guidelines</div>
          <div className="w-12 h-px bg-[#C9A055] mx-auto mb-6" />
          <p className="font-body text-[15px] leading-[1.85] text-[#B5B0A7]">
            {page.guidelines}
          </p>
        </div>
      </section>

      {/* SUBMIT MODAL */}
      {submitOpen && <SubmitModal page={page} onClose={() => setSubmitOpen(false)} onSubmitted={() => { setSubmitOpen(false); toast.success("Truth submitted. Thank you."); }} />}
    </Layout>
  );
}

function TruthCard({ t, voted, showVotes, votingEnabled, onVote }) {
  return (
    <div className="bg-[#050505] border border-white/10 p-5 flex flex-col hover:border-[#C9A055]/40 transition-colors group" data-testid={`tw-card-${t.id}`}>
      {t.featured && (
        <div className="inline-flex items-center gap-1.5 font-ui text-[9px] tracking-[0.22em] uppercase text-[#C9A055] mb-2 self-start">
          <Star size={10} fill="#C9A055" /> Featured
        </div>
      )}
      <Link to={`/w/${t.id}`} data-testid={`tw-card-open-${t.id}`} className="block">
        <Quote size={16} className="text-[#C9A055] opacity-60 mb-2" />
        <div className="font-display text-[17px] text-[#F4F1EA] leading-snug group-hover:text-[#F4F1EA]">&ldquo;{t.text}&rdquo;</div>
        {t.story && (
          <p className="font-body text-[13px] text-[#8a857d] mt-3 line-clamp-3 italic">{t.story}</p>
        )}
        {t.story && (
          <span className="inline-block mt-2 font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055]">Read the full story →</span>
        )}
      </Link>
      <div className="mt-auto pt-4 flex items-center justify-between">
        <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d]">
          {t.name || "Anonymous"}{t.city ? ` · ${t.city}${t.state ? `, ${t.state}` : ""}` : ""}
        </div>
        {votingEnabled && (
          <button
            onClick={onVote}
            data-testid={`tw-vote-${t.id}`}
            className={`flex items-center gap-1.5 font-ui text-[11px] tracking-[0.12em] px-2 py-1 border transition ${voted ? "border-[#C9A055] text-[#C9A055]" : "border-white/15 text-[#8a857d] hover:text-[#C9A055] hover:border-[#C9A055]"}`}
          >
            <Heart size={11} fill={voted ? "#C9A055" : "none"} />
            {showVotes ? (t.votes ?? 0) : "Vote"}
          </button>
        )}
      </div>
    </div>
  );
}

function SubmitModal({ page, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    text: "", story: "", name: "", email: "", city: "", state: "",
    consent_edit: false, consent_feature: false, consent_artwork: false, anonymous: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.text.trim() || form.text.trim().length < 4) {
      toast.error("Share a truth (at least a few words).");
      return;
    }
    if (!form.consent_edit || !form.consent_feature) {
      toast.error("Please confirm the editing and feature consents.");
      return;
    }
    setBusy(true);
    try {
      await http.post("/truth-wall/submit", {
        text: form.text.trim(),
        story: form.story.trim() || null,
        name: form.anonymous ? null : (form.name.trim() || null),
        email: form.email.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        consent_edit: form.consent_edit,
        consent_feature: form.consent_feature,
        consent_artwork: form.consent_artwork,
        anonymous: form.anonymous,
      });
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="tw-submit-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 max-w-xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-1">Truth Wall</div>
            <div className="font-display text-xl text-[#F4F1EA]">Submit a Truth</div>
          </div>
          <button type="button" onClick={onClose} className="text-[#8a857d] hover:text-[#F4F1EA]"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <Field label="Your truth (one line is enough)">
            <textarea
              data-testid="tw-form-text"
              className="stc-input min-h-[80px]"
              maxLength={240}
              value={form.text}
              onChange={(e) => set("text", e.target.value)}
              placeholder="The right people stay."
            />
            <div className="text-right text-[10px] text-[#8a857d] mt-1">{form.text.length}/240</div>
          </Field>
          <Field label="Optional story behind the truth">
            <textarea data-testid="tw-form-story" className="stc-input min-h-[80px]" maxLength={2000}
                      value={form.story} onChange={(e) => set("story", e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="tw-form-name" className="stc-input" placeholder="Name" value={form.name}
                   disabled={form.anonymous} onChange={(e) => set("name", e.target.value)} maxLength={80} />
            <input data-testid="tw-form-email" className="stc-input" placeholder="Email (optional)" value={form.email}
                   onChange={(e) => set("email", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="tw-form-city" className="stc-input" placeholder="City (optional)" value={form.city}
                   onChange={(e) => set("city", e.target.value)} maxLength={80} />
            <input data-testid="tw-form-state" className="stc-input" placeholder="State (optional)" value={form.state}
                   onChange={(e) => set("state", e.target.value)} maxLength={80} />
          </div>
          {page.allow_anonymous && (
            <label className="flex items-center gap-2 font-body text-sm text-[#B5B0A7] cursor-pointer">
              <input type="checkbox" checked={form.anonymous} onChange={(e) => set("anonymous", e.target.checked)}
                     data-testid="tw-form-anon" className="accent-[#C9A055]" />
              Submit anonymously
            </label>
          )}

          <div className="border-t border-white/10 pt-3 space-y-2 font-body text-[13px] text-[#B5B0A7]">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={form.consent_edit} onChange={(e) => set("consent_edit", e.target.checked)}
                     data-testid="tw-consent-edit" className="accent-[#C9A055] mt-1" />
              I understand my submission may be lightly edited for spelling, grammar, length, or clarity while keeping the original meaning.
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={form.consent_feature} onChange={(e) => set("consent_feature", e.target.checked)}
                     data-testid="tw-consent-feature" className="accent-[#C9A055] mt-1" />
              I give Simple Truths Club permission to display, share, publish, and use this submission as part of the movement.
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={form.consent_artwork} onChange={(e) => set("consent_artwork", e.target.checked)}
                     data-testid="tw-consent-artwork" className="accent-[#C9A055] mt-1" />
              I understand selected truths may inspire future artwork, products, stories, or public displays.
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/10">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button type="submit" disabled={busy} data-testid="tw-form-submit" className="btn-gold flex items-center gap-2 disabled:opacity-60">
            <Sparkles size={14} /> {busy ? "Submitting…" : "Submit my truth"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
